import { useCallback, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, Tag, Typography, Select, Button, Tooltip, theme, Progress, Input, DatePicker, Space, Checkbox, message, Row, Col } from '../../ui';
import { StatCard, statTone } from '../../ui';
import type { TagTone } from '../../ui';
import { useNavigate } from 'react-router-dom';
import { useUrlString, useUrlBool, useUrlPartialRangeOrNull } from '../../hooks/useUrlState';
import { Plus, Download, FileText, Wallet, AlertCircle, Clock } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request, api } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { previewPdf } from '../../lib/pdf';
import { usePermission, useAuthStore } from '../../store/auth';
import { ExportDialog } from '../../components/ExportDialog';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';
import { RetailerLink, OrderLink } from '../../components/EntityLinks';
import { TerritoryChips } from '../../components/TerritoryChips';
import { useDebounce } from '../../hooks/useDebounce';
import { useServerSort } from '../../hooks/useServerSort';
import { displayMobile } from '../../lib/validators';
import { PaymentFormModal } from './PaymentFormModal';
import { inr2 } from '../../lib/format';
import { PALETTE } from '../../lib/palette';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { inr as inrWhole } from '../../lib/format';

const STATUS_COLOR: Record<string, TagTone> = {
  OPEN: 'blue', PARTIAL: 'gold', PAID: 'green', OVERDUE: 'red', CANCELLED: 'neutral',
};
// The shared formatter: a negative reads "-₹480", never "₹-480".
const inr = inrWhole;
// Money that hides trailing ".00": shows 2 decimals only when there are non-zero paise (₹4,65,750 but ₹1,274.50).
const inrT = inr2;

export function InvoicesListPage() {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const canRecordPayment = usePermission('PAYMENT.CREATE');
  // Matches the server's INVOICE.EXPORT gate — the register carries the GST breakup, so it is granted
  // separately from INVOICE.VIEW.
  const canExport = usePermission('INVOICE.EXPORT');
  const [exportOpen, setExportOpen] = useState(false);

  // Open the invoice PDF: the uploaded Tally PDF if present (legacy), else the
  // in-app generated tax-invoice PDF rendered on demand.
  const openInvoicePdf = async (r: any) => {
    if (r.pdfUrl) { window.open(r.pdfUrl, '_blank', 'noopener'); return; }
    try {
      await previewPdf(() => api.get(`/invoices/${r.id}/pdf`, { responseType: 'blob' }));
    } catch {
      message.error('Failed to load invoice PDF');
    }
  };
  const [status, setStatus] = useUrlString<string | undefined>('status', undefined);
  const [searchInput, setSearchInput] = useUrlString('q');
  const search = useDebounce(searchInput, 400);
  // In the URL like the rest of them: a filter held in component state is gone the moment you open an
  // invoice and press Back, and — now that the export carries the page's filters — a filter the URL
  // does not hold is one a shared link cannot reproduce.
  const [orderSearchInput, setOrderSearchInput] = useUrlString('orderNo');
  const orderSearch = useDebounce(orderSearchInput, 400);
  const [retailerId, setRetailerId] = useUrlString<string | undefined>('retailer', undefined);
  // The RangePicker emits [start, null] on the first click (before the end date is picked), so the
  // endpoints are held nullable — range[1].format() on a half-picked range crashes the page.
  // Each bound then filters ON ITS OWN ("billed since 1 Aug", "up to 31 Aug"): the server applies
  // from and to independently, and requiring both meant a half-filled range silently filtered
  // nothing — which, now that the export carries these dates, would be a file holding more than
  // the screen it came from.
  const [range, setRange] = useUrlPartialRangeOrNull('range');
  const [balance, setBalance] = useUrlString<string | undefined>('balance', undefined);
  const [overdue, setOverdue] = useUrlBool('overdue');
  const [paymentOpen, setPaymentOpen] = useState(false);
  // The Retailer column's key is 'retailer' (it renders a composed block, not one field); the server
  // orders it by the joined retailer name.
  const { sortState, onSortChange, sort } = useServerSort({ fieldMap: { retailer: 'retailerName' } });

  const params = useMemo(() => {
    const p: Record<string, any> = {};
    if (status) p.status = status;
    if (search.trim()) p.search = search.trim();
    if (orderSearch.trim()) p.orderNo = orderSearch.trim();
    if (retailerId) p.retailerId = retailerId;
    if (range?.[0]) p.from = range[0].format('YYYY-MM-DD');
    if (range?.[1]) p.to = range[1].format('YYYY-MM-DD');
    if (balance) p.balance = balance;
    if (overdue) p.overdue = 'true';
    if (sort) p.sort = sort;
    return p;
  }, [status, search, orderSearch, retailerId, range, balance, overdue, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', params],
    queryFn: () => request<any[]>({ url: '/invoices', params }),
    placeholderData: keepPreviousData,
  });

  // Resolve retailerId -> retailer details for the table, the retailer filter, AND each retailer's ledger
  // balance (needed to net invoice gross down to the reconciled account figure). Gated on RETAILER.VIEW.
  const { data: retailers } = useQuery({
    // Wide fetch so most invoices resolve to a name; full coverage for very large
    // retailer bases ultimately wants the invoice payload enriched server-side.
    queryKey: ['invoice-retailer-map'],
    queryFn: () => requestAllPages('/retailers'),
    enabled: canViewRetailers,
  });
  const retailerMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of retailers?.data ?? []) m.set(String(r.id), r);
    return m;
  }, [retailers]);

  // Invoice balances are GROSS of sales-return credit notes — a return posts advance-in-credit to the retailer
  // LEDGER, not to the invoice. So documents here (and the invoice / proforma) stay GROSS and consistent with
  // each other; the return credit is surfaced SEPARATELY and traceably (banner → Credit Notes). Only the
  // RETAILER's own unfiltered view folds their credit-in-hand into their invoices (oldest first) so it matches
  // the account statement they see. Account-level "Outstanding" (dashboard / aging / chatbot) is the ledger
  // net; here we also show that net receivable next to the gross, with the credit traced to its source.
  const round2 = (n: number) => Math.round((n || 0) * 100) / 100;
  const role = useAuthStore((s) => s.user?.role);
  const isRetailerView = role === 'RETAILER';
  const unfiltered = !status && !search.trim() && !orderSearch.trim() && !retailerId && !range && !balance && !overdue;
  const { data: meRetailer } = useQuery({
    queryKey: ['invoices-me-retailer'],
    queryFn: () => request<any>({ url: '/retailers/me' }),
    enabled: isRetailerView,
  });
  // Ledger (net account) balance for a retailer: the logged-in retailer's own, or from the staff retailer map.
  const ledgerFor = (rid: any): number | undefined => {
    if (isRetailerView) { const v = Number(meRetailer?.outstandingBalance); return Number.isNaN(v) ? undefined : v; }
    const r = retailerMap.get(String(rid));
    return r && r.outstandingBalance != null ? Number(r.outstandingBalance) : undefined;
  };

  // Rows are GROSS by default; we also compute the total return-credit-in-hand across the shown retailers
  // (for the banner + net-receivable). `creditInHand` never invents credit — it's each retailer's
  // (gross open invoices − net ledger owed), i.e. exactly the return credit they hold. On the RETAILER's own
  // unfiltered view that credit is additionally folded into their invoices (statement match); staff stay gross.
  // Shared builder so BOTH the table and the (category-unfiltered) KPI set net credit the same way.
  const buildDisplay = useCallback((raw: any[] | undefined, isUnfiltered: boolean) => {
    const r2 = (n: number) => Math.round((n || 0) * 100) / 100;
    const ledger = (rid: any): number | undefined => {
      if (isRetailerView) { const v = Number(meRetailer?.outstandingBalance); return Number.isNaN(v) ? undefined : v; }
      const rr = retailerMap.get(String(rid));
      return rr && rr.outstandingBalance != null ? Number(rr.outstandingBalance) : undefined;
    };
    const rows = (raw ?? []).map((r: any) => ({ ...r, creditApplied: 0, netBalance: r2(Number(r.balanceAmt) || 0) }));
    let gross = 0;
    for (const r of rows) if (r.status !== 'CANCELLED' && r.netBalance > 0.009) gross = r2(gross + r.netBalance);
    if (!isUnfiltered) return { displayRows: rows, creditInHand: 0, grossOutstanding: gross };
    const groups = new Map<string, any[]>();
    for (const r of rows) { if (r.status === 'CANCELLED' || r.netBalance <= 0.009) continue; const k = String(r.retailerId); if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(r); }
    let totalCredit = 0;
    for (const [rid, group] of groups) {
      const owed = ledger(rid);
      if (owed === undefined) continue; // no ledger available → this retailer stays gross, no credit claimed
      const g = group.reduce((s: number, r: any) => s + r.netBalance, 0);
      const credit = Math.max(0, r2(g - Math.max(0, owed))); // return credit held on the account
      if (credit <= 0.009) continue;
      totalCredit = r2(totalCredit + credit);
      if (isRetailerView) { // fold into the retailer's OWN invoices (statement match); staff rows stay gross
        group.sort((a: any, b: any) => String(a.invoiceDate).localeCompare(String(b.invoiceDate)) || (Number(a.id) - Number(b.id)));
        let rem = credit;
        for (const r of group) { if (rem <= 0.009) break; const ap = Math.min(rem, r.netBalance); r.creditApplied = r2(ap); r.netBalance = r2(r.netBalance - ap); rem = r2(rem - ap); }
      }
    }
    return { displayRows: rows, creditInHand: r2(totalCredit), grossOutstanding: gross };
  }, [retailerMap, meRetailer, isRetailerView]);

  // TABLE rows use the full (all-filters) query. KPI tiles + credit banner use a fetch with the status/balance/
  // overdue CATEGORY filters dropped, so applying a filter (or clicking a tile) narrows the table WITHOUT
  // collapsing the tiles; scope filters (retailer/date/search) still apply.
  const { displayRows } = useMemo(() => buildDisplay(data, unfiltered), [buildDisplay, data, unfiltered]);
  const kpiParams = useMemo(() => { const { status: _s, balance: _b, overdue: _o, sort: _so, ...rest } = params; return rest; }, [params]);
  const { data: kpiData } = useQuery({ queryKey: ['invoices-kpi', kpiParams], queryFn: () => request<any[]>({ url: '/invoices', params: kpiParams }), placeholderData: keepPreviousData });
  const kpiUnfiltered = !search.trim() && !orderSearch.trim() && !retailerId && !range;
  const kpiAgg = useMemo(() => buildDisplay(kpiData, kpiUnfiltered), [buildDisplay, kpiData, kpiUnfiltered]);
  const creditInHand = kpiAgg.creditInHand;
  const grossOutstanding = kpiAgg.grossOutstanding;

  const applyCredit = isRetailerView && creditInHand > 0.009; // only the retailer's own rows are netted
  const netReceivable = round2(grossOutstanding - creditInHand); // gross unpaid − return credit held

  const kpis = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let invoiced = 0, cashPaid = 0, outstanding = 0, overdueAmt = 0, openCount = 0, overdueCount = 0;
    for (const i of kpiAgg.displayRows) {
      // A voided invoice (CANCELLED) is not live invoiced revenue — its ledger debit was reversed on
      // void — so exclude it from Invoiced/Collected/count too, not just from outstanding/overdue.
      if (i.status === 'CANCELLED') continue;
      invoiced += Number(i.grandTotal || 0);
      cashPaid += Number(i.paidAmt || 0);
      const bal = Number(i.netBalance || 0);
      if (bal > 0.009) {
        outstanding += bal; openCount++;
        if (i.dueDate && new Date(i.dueDate) < today) { overdueAmt += bal; overdueCount++; }
      }
    }
    invoiced = round2(invoiced); outstanding = round2(outstanding);
    // Retailer view: "settled" = payments + return credits (matches the statement's "Paid / credited"); for
    // staff it stays cash collected. invoiced − outstanding folds in the credit exactly.
    const settled = applyCredit ? round2(invoiced - outstanding) : round2(cashPaid);
    const count = kpiAgg.displayRows.filter((i: any) => i.status !== 'CANCELLED').length;
    return { count, invoiced, settled, outstanding, overdueAmt: round2(overdueAmt), openCount, overdueCount };
  }, [kpiAgg, applyCredit]);

  const columns: DataTableColumn<any>[] = [
    { key: 'invoiceNo', title: 'Invoice No', dataIndex: 'invoiceNo', sortable: true, alwaysVisible: true, width: 180 },
    {
      // Linked order number as its own clickable column (mirrors the Payments list). Standalone /
      // counter-sale invoices have no order → em dash.
      key: 'orderNo', title: 'Order No', width: 170, sortable: true,
      render: (_: any, r: any) =>
        r.orderNo ? <OrderLink id={r.orderId}>{r.orderNo}</OrderLink> : <span style={{ color: token.colorTextTertiary }}>—</span>,
    },
    {
      key: 'retailer', title: 'Retailer', alwaysVisible: true, width: 320, sortable: true,
      render: (_: any, r: any) => {
        // Prefer the name enriched on the invoice payload (always present); fall back
        // to the client-side retailer map, then the bare id.
        const ret = retailerMap.get(String(r.retailerId));
        const name = r.retailerName ?? ret?.companyName;
        const code = r.retailerCode ?? ret?.code;
        const city = r.retailerCity ?? ret?.city;
        const mobile = r.retailerMobile ?? ret?.mobile;
        return name ? (
          <div>
            {/* Identity on the first line — the name, then the dealer code and mobile, which are
                what this desk quotes and dials. WHERE they are goes on the second, as chips; the
                town used to be the tail of a dot-joined string with no level attached to it. Two
                lines, as before: a third would be a third on every row of the register. */}
            <div className="flex min-w-0 items-baseline gap-1.5">
              <span className="truncate" style={{ fontWeight: 600 }}><RetailerLink id={r.retailerId}>{name}</RetailerLink></span>
              <span className="shrink-0" style={{ fontSize: 12, color: token.colorTextTertiary }}>
                {code}{mobile ? ` · ${displayMobile(mobile)}` : ''}
              </span>
            </div>
            <TerritoryChips territoryId={r.retailerTerritoryId} city={city} hideWhenEmpty />
          </div>
        ) : <span style={{ color: token.colorTextTertiary }}>#{r.retailerId}</span>;
      },
    },
    { key: 'invoiceDate', title: 'Date', dataIndex: 'invoiceDate', sortable: true, width: 130 },
    { key: 'dueDate', title: 'Due', dataIndex: 'dueDate', sortable: true, width: 130 },
    {
      key: 'grandTotal', title: 'Grand Total', dataIndex: 'grandTotal', width: 150,
      sortable: true, render: (v: any) => inrT(v),
    },
    { key: 'paidAmt', title: 'Paid', dataIndex: 'paidAmt', sortable: true, width: 140, render: (v: any) => inrT(v) },
    {
      key: 'balanceAmt', title: 'Balance', dataIndex: 'netBalance', width: 150,
      // Sorted server-side on the invoice's own balance — which IS what this column shows everywhere
      // except the retailer's own credit-netted view, where netBalance is computed here and no DB
      // column holds it. There the header simply isn't offered, rather than ordering the register by
      // a number the reader cannot see.
      sortable: !applyCredit,
      // netBalance = invoice balance after applying the retailer's return credit-in-hand (retailer view); for
      // staff it equals the gross balance. A credited row shows how much credit offset it.
      render: (v: any, r: any) => (
        <div>
          <b style={{ color: Number(v) > 0 ? PALETTE.danger[500] : PALETTE.success[500] }}>{inrT(v)}</b>
          {Number(r.creditApplied) > 0 && <div style={{ fontSize: 11, color: 'var(--color-brand-600)' }}>−{inrT(r.creditApplied)} credit</div>}
        </div>
      ),
    },
    {
      key: 'progress', title: 'Payment', width: 200, alwaysVisible: true,
      render: (_: any, r: any) => {
        const total = Number(r.grandTotal) || 0;
        const paid = Number(r.paidAmt) || 0;
        const credit = Number(r.creditApplied) || 0;
        const covered = paid + credit; // cash + return credit applied
        const pct = total > 0 ? Math.min(100, Math.round((covered / total) * 100)) : 0;
        const strokeColor = pct >= 100 ? PALETTE.success[500] : pct > 0 ? PALETTE.harvest[400] : PALETTE.danger[500];
        return (
          <Tooltip label={`Paid ${inrT(paid)}${credit > 0 ? ` + credit ${inrT(credit)}` : ''} of ${inrT(total)} · balance ${inrT(r.netBalance)}`}>
            <Progress percent={pct} size="small" strokeColor={strokeColor} />
          </Tooltip>
        );
      },
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true, width: 130,
      render: (s: string) => <Tag tone={STATUS_COLOR[s]}>{s}</Tag>,
    },
    {
      key: 'pdf', title: 'PDF', width: 70, align: 'center', alwaysVisible: true,
      render: (_: any, r: any) => (
        <Tooltip label="Download invoice PDF">
          <Button
            variant="text"
            aria-label="Download invoice PDF"
            icon={<Download size={16} />}
            onClick={(e) => { e.stopPropagation(); openInvoicePdf(r); }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Invoices</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Space wrap>
            {canExport && (
              <Button size="sm" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
            )}
            {canRecordPayment ? (
              <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setPaymentOpen(true)}>
                Record Payment
              </Button>
            ) : (
              <Tooltip label="You don't have permission to record payments (requires PAYMENT.CREATE)">
                <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                  <Button variant="primary" size="sm" icon={<Plus size={16} />} disabled style={{ pointerEvents: 'none' }}>
                    Record Payment
                  </Button>
                </span>
              </Tooltip>
            )}
          </Space>
        </div>
      </div>

      <Row align="stretch" gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Kpi title="Invoiced" value={inr(kpis.invoiced)} sub={`${kpis.count} invoice${kpis.count === 1 ? '' : 's'}`} color={PALETTE.sky[500]} icon={<FileText size={16} />}
            active={!balance && !overdue} onClick={() => { setBalance(undefined); setOverdue(false); }} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi title={applyCredit ? 'Paid / credited' : 'Collected'} value={inr(kpis.settled)} sub={applyCredit ? 'payments + return credits' : 'payments received'} color={PALETTE.brand[600]} icon={<Wallet size={16} />}
            active={balance === 'PAID'} onClick={() => { setOverdue(false); setBalance(balance === 'PAID' ? undefined : 'PAID'); }} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi
            title={isRetailerView ? 'Outstanding' : 'Unpaid invoices'}
            value={inr(kpis.outstanding)}
            sub={isRetailerView
              ? (applyCredit && creditInHand > 0.01 ? 'net of return credit' : `${kpis.openCount} open`)
              : (creditInHand > 0.01 ? `gross · net ${inr(netReceivable)} after credit` : `${kpis.openCount} open`)}
            color={PALETTE.harvest[400]} icon={<AlertCircle size={16} />}
            active={balance === 'OUTSTANDING' && !overdue} onClick={() => { setOverdue(false); setBalance(balance === 'OUTSTANDING' ? undefined : 'OUTSTANDING'); }} />
        </Col>
        <Col xs={12} md={6}>
          <Kpi title="Overdue" value={inr(kpis.overdueAmt)} sub={`${kpis.overdueCount} past due`} color={PALETTE.danger[500]} icon={<Clock size={16} />}
            active={overdue} onClick={() => { const next = !overdue; setOverdue(next); if (next) setBalance(undefined); }} />
        </Col>
      </Row>

      {/* Retailer's own view: their return credit is folded into their invoices so it matches their statement. */}
      {applyCredit && creditInHand > 0.01 && (
        <div className="mb-3 rounded-card border border-success-500/25 bg-success-500/10 px-4 py-2.5 text-sm text-content">
          <span className="font-semibold text-success-600">{inrT(creditInHand)} return credit</span> applied against your open invoices — the figures below are net of it and match your <span className="font-semibold">account statement</span>.
        </div>
      )}

      <Card>
        <DataTable
          storageKey="invoices-list"
          rowKey="id"
          layoutFixed
          loading={isLoading}
          dataSource={displayRows}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          onRow={(i: any) => ({
            onClick: () => navigate(`/billing/invoices/${i.id}`),
            style: { cursor: 'pointer' },
          })}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          toolbarLeft={
            <Space wrap>
              {/* Filters follow the column order: Invoice no → Order no → Retailer → Date → Balance → Status → Overdue. */}
              <Input placeholder="Invoice no" style={{ width: 150 }}
                value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
              <Input placeholder="Order no" style={{ width: 150 }}
                value={orderSearchInput} onChange={(e) => setOrderSearchInput(e.target.value)} />
              {canViewRetailers && (
                <Select placeholder="Retailer" allowClear {...CUSTOMER_SELECT_PROPS} style={{ width: 200 }}
                  value={retailerId} onChange={setRetailerId}
                  options={(retailers?.data ?? []).map((r: any) => customerOption(r))} />
              )}
              <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => setRange(r)} />
              <Select placeholder="Balance" allowClear style={{ width: 150 }} value={balance} onChange={setBalance}
                options={[{ value: 'OUTSTANDING', label: 'Has balance' }, { value: 'PAID', label: 'Fully paid' }]} />
              <Select placeholder="Status" allowClear style={{ width: 140 }} value={status} onChange={setStatus}
                options={Object.keys(STATUS_COLOR).map((s) => ({ value: s, label: s }))} />
              <Checkbox checked={overdue} onCheckedChange={(checked) => setOverdue(checked)} label="Overdue only" />
            </Space>
          }
        />
      </Card>
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export invoices"
        description="The rows and order shown on this page. Excel carries the GST breakup for filing."
        baseUrl="/invoices/export"
        /* The dialog's own period picker is gone (`hidePeriod`): it asked for a date range the toolbar
           right behind it had already set, the two could disagree, and the dialog won — so an empty
           picker exported all time while the screen showed one week. With it hidden the page's from/to
           travel through `baseParams` untouched, alongside every other filter AND `sort`, so the file
           is exactly the register on screen — same slice, same ascending/descending order. */
        hidePeriod
        baseParams={params}
      />
      <PaymentFormModal open={paymentOpen} onClose={() => setPaymentOpen(false)} />
    </div>
  );
}

/** Delegates to the shared StatCard (see DispatchesPage). Also replaces the hand-rolled
 *  role="button" + onKeyDown keyboard handling — StatCard renders a real <button> when clickable,
 *  so Enter/Space and focus come from the platform rather than being re-implemented here. */
function Kpi({ title, value, sub, color, icon, onClick, active }: { title: string; value: React.ReactNode; sub?: string; color: string; icon: React.ReactNode; onClick?: () => void; active?: boolean }) {
  return (
    <StatCard
      label={title}
      value={value}
      hint={sub}
      tone={statTone(color)}
      icon={icon}
      onClick={onClick}
      active={active}
    />
  );
}
